/**
 * TODO: Move all of this state either into auth-slice or a new payment-slice for stripe stuff.
 */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
  OrgInvite,
  User,
  ApiKey,
  OrgOnboarding,
  OrgOnboardingStep,
  OrgProspectPropertiesMeta,
  StoredOrgProspectProperty,
} from 'types';
import {
  getTeam,
  getTeamInvites,
  removeTeamInvite,
  sendTeamInvite,
  updatePaymentPlan,
  getApiKeys,
  postApiKey,
  putApiKey,
  deleteApiKey,
  getAllProperties,
  markOrgOnboardingStepComplete,
  getOrgOnboardingState,
} from 'shared/api';
import type { AppState } from './reducers';
import type { RejectError } from 'shared/api/axios';
import {
  addDomain,
  checkAccount,
  removeDomain,
  removeMember,
  saveClearbitIntegration,
  saveOrgIntegration,
  updateTeamMember,
  updateOrgIntegration,
  updateOrgSettings,
  saveChiliPiperIntegration,
  saveSixsenseIntegration,
  saveHubSpotMeetingsIntegration,
} from './shared-actions';
import { addManyCases } from 'shared/utils/redux';
import { AllProperties } from 'types/property';
import { selectActiveOrg, selectIsAdmin } from 'shared/store/auth-slice';
import { DEFAULT_FIELD_META } from 'browser/components/Settings/Integrations/HubspotConfigurationPage/HubspotMapping/constants';
import { DEFAULT_PROSPECT_PROPERTIES } from 'browser/components/Outbound/useOutbound';

// STATE
export const initialState = {
  apiKeys: [] as ApiKey[],
  hasLoadedApiKeys: false,
  isLoadingApiKeys: false,
  loadApiKeysError: null as null | string,

  onboarding: { complete: false, steps: {} } as OrgOnboarding,

  invites: [] as OrgInvite[],
  hasLoadedInvites: false,
  isLoadingInvites: false,
  loadInvitesError: null as null | string,

  hasLoadedUsers: false,
  isLoadingUsers: false,
  loadUsersError: null as null | string,

  hasLoadedDomains: false,
  isLoadingDomains: false,
  loadDomainsError: null as null | string,

  hasAddedDomain: false,
  isAddingDomain: false,
  addDomainError: null as null | string,

  hasRemovedDomain: false,
  isRemovingDomain: false,
  removeDomainError: null as null | string,

  isRemovingInvite: false,
  hasRemovedInvite: false,
  removeInviteError: null as null | string,
  removingInviteEmail: null as null | string,

  hasSavedOrgIntegration: false,
  isSavingOrgIntegrations: false,
  saveOrgIntegrationError: null as null | string,

  isUpdatingSettings: false,
  hasUpdatedSettings: false,
  updateSettingsError: null as null | string,

  isRemovingMember: false,
  removeMemberError: null as null | string,

  customProperties: { contact: [], company: [], deal: [] } as AllProperties,

  orgProspectProperties: [] as StoredOrgProspectProperty[],
  orgPropspectPropertiesMeta: null as OrgProspectPropertiesMeta | null,
};

export type OrgState = typeof initialState;

// THUNKS
export const addApiKey = createAsyncThunk<
  { apiKey: ApiKey; secretKey: string },
  Parameters<typeof postApiKey>[0],
  { state: AppState; rejectValue: RejectError }
>('org/addApiKey', async (args, thunkApi) => {
  const res = await postApiKey(args);
  thunkApi.dispatch(loadApiKeys());
  return res.data;
});

export const loadApiKeys = createAsyncThunk<
  ApiKey[],
  void,
  { state: AppState; rejectValue: RejectError }
>('org/loadApiKeys', async () => {
  const res = await getApiKeys();
  return res.data;
});

export const updateApiKey = createAsyncThunk<
  void,
  {
    apiKeyId: string;
    name: string;
    active: boolean;
  },
  { state: AppState; rejectValue: RejectError }
>('org/updateApiKey', async (args, thunkApi) => {
  await putApiKey(args);
  thunkApi.dispatch(loadApiKeys());
});

export const removeApiKey = createAsyncThunk<
  void,
  string,
  { state: AppState; rejectValue: RejectError }
>('org/removeApiKey', async (apiKeyId, thunkApi) => {
  await deleteApiKey(apiKeyId);
  thunkApi.dispatch(loadApiKeys());
});

export const inviteUser = createAsyncThunk<
  void,
  Parameters<typeof sendTeamInvite>[0],
  { state: AppState; rejectValue: RejectError }
>('org/inviteUser', async (args, thunkApi) => {
  await sendTeamInvite(args);
  thunkApi.dispatch(loadInvites());
});

export const removeInvite = createAsyncThunk<
  void,
  string,
  { state: AppState; rejectValue: RejectError }
>('org/removeInvite', async (emailAddress, thunkApi) => {
  await removeTeamInvite(emailAddress);
  thunkApi.dispatch(loadInvites());
});

export const loadInvites = createAsyncThunk<
  OrgInvite[],
  void,
  { state: AppState; rejectValue: RejectError }
>('org/loadInvites', async () => {
  const res = await getTeamInvites();
  return res.data;
});

export const loadUsers = createAsyncThunk<
  User[],
  void,
  { state: AppState; rejectValue: RejectError }
>('org/loadUsers', async () => {
  const res = await getTeam();
  return res.data;
});

export const updatePlan = createAsyncThunk<
  void,
  string,
  { state: AppState; rejectValue: RejectError }
>('org/updatePlan', async (planKind, thunkApi) => {
  const { data } = await updatePaymentPlan(planKind);
  if (data.success) {
    thunkApi.dispatch(checkAccount());
  }
});

export const loadCustomProperties = createAsyncThunk<
  AllProperties,
  void,
  { state: AppState; rejectValue: RejectError }
>('org/loadCustomProperties', async () => {
  const res = await getAllProperties();
  return res.data;
});

export const loadOrgOutboundSettings = createAsyncThunk<
  void,
  void,
  { state: AppState; rejectValue: RejectError }
>('org/loadOrgOutboundSettings', async (_, { dispatch, getState }) => {
  const state = getState();
  const activeOrg = selectActiveOrg(state);
  const res = (await getAllProperties()).data;
  const propertiesMeta: OrgProspectPropertiesMeta = {
    contact: {},
    company: {},
  };

  // Populate default propery meta
  Object.keys(DEFAULT_FIELD_META.contact).forEach(propertyKey => {
    const defaultProperty = DEFAULT_FIELD_META.contact[propertyKey];
    propertiesMeta.contact[propertyKey] = {
      displayName: defaultProperty?.label,
      type: defaultProperty?.type,
      isCustom: false,
    };
  });
  Object.keys(DEFAULT_FIELD_META.company).forEach(propertyKey => {
    const defaultProperty = DEFAULT_FIELD_META.company[propertyKey];
    propertiesMeta.company[propertyKey] = {
      displayName: defaultProperty?.label,
      type: defaultProperty?.type,
      isCustom: false,
    };
  });

  // Populate custom property meta
  res.contact?.forEach(property => {
    propertiesMeta.contact[property.key] = {
      displayName: property.displayName,
      type: property.type,
      isCustom: true,
    };
  });

  res.company?.forEach(property => {
    propertiesMeta.company[property.key] = {
      displayName: property.displayName,
      type: property.type,
      isCustom: true,
    };
  });

  dispatch(setOrgProspectPropertiesMeta(propertiesMeta));
  dispatch(
    setOrgProspectProperties(
      activeOrg?.outboundProspectProperties ||
        // Map the defaults to the data format in org.outboundProspectProperties
        DEFAULT_PROSPECT_PROPERTIES.map(defaultProperty => ({
          propertyKey: defaultProperty.propertyKey,
          propertyGroup: 'contact',
          label: defaultProperty.displayName,
        })),
    ),
  );
});

export const completeOrgOnboardingStep = createAsyncThunk<
  OrgOnboarding,
  OrgOnboardingStep,
  { state: AppState; rejectValue: RejectError }
>('org/completeOnboardingStep', async (step, { getState, rejectWithValue }) => {
  const state = getState();
  const isAdmin = selectIsAdmin(state);

  if (isAdmin && !state.org.onboarding.steps[step]) {
    const res = await markOrgOnboardingStepComplete(step);
    return res.data;
  }
  return rejectWithValue({ message: 'Onboarding step already completed' });
});

export const refreshOrgOnboarding = createAsyncThunk<
  OrgOnboarding,
  void,
  { state: AppState; rejectValue: RejectError }
>('org/refreshOnboarding', async () => {
  const res = await getOrgOnboardingState();
  return res.data;
});

// SLICE
const orgSlice = createSlice({
  name: 'org',
  initialState,
  reducers: {
    clearInviteUserEphemeralState(state) {
      state.invites = [];
      state.isLoadingInvites = false;
      state.hasLoadedInvites = false;
      state.loadInvitesError = null;
      state.isRemovingInvite = false;
      state.hasRemovedInvite = false;
    },
    setOrgProspectProperties(state, action: PayloadAction<StoredOrgProspectProperty[]>) {
      state.orgProspectProperties = action.payload;
    },
    setOrgProspectPropertiesMeta(
      state,
      action: PayloadAction<OrgProspectPropertiesMeta>,
    ) {
      state.orgPropspectPropertiesMeta = action.payload;
    },
  },
  extraReducers: builder => {
    // loadApiKeys
    builder.addCase(loadApiKeys.pending, state => {
      state.isLoadingApiKeys = true;
      state.loadApiKeysError = null;
    });
    builder.addCase(loadApiKeys.fulfilled, (state, action) => {
      state.apiKeys = action.payload;
      state.isLoadingApiKeys = false;
      state.hasLoadedApiKeys = true;
    });
    builder.addCase(loadApiKeys.rejected, (state, action) => {
      state.isLoadingApiKeys = false;
      state.loadApiKeysError = action.error.message || null;
    });

    // loadInvites
    builder.addCase(loadInvites.pending, state => {
      state.isLoadingInvites = true;
      state.loadInvitesError = null;
    });
    builder.addCase(loadInvites.fulfilled, (state, action) => {
      state.invites = action.payload;
      state.isLoadingInvites = false;
      state.hasLoadedInvites = true;
    });
    builder.addCase(loadInvites.rejected, (state, action) => {
      state.isLoadingInvites = false;
      state.loadInvitesError = action.error.message || null;
    });

    // refreshOrgOnboarding
    builder.addCase(refreshOrgOnboarding.fulfilled, (state, action) => {
      state.onboarding = action.payload;
    });
    builder.addCase(completeOrgOnboardingStep.fulfilled, (state, action) => {
      state.onboarding = action.payload;
    });

    // loadUsers
    builder.addCase(loadUsers.pending, state => {
      state.isLoadingUsers = true;
      state.loadUsersError = null;
    });
    builder.addCase(loadUsers.fulfilled, state => {
      state.isLoadingUsers = false;
      state.hasLoadedUsers = true;
    });
    builder.addCase(loadUsers.rejected, (state, action) => {
      state.isLoadingUsers = false;
      state.loadUsersError = action.error.message || null;
    });

    // removeInvite
    builder.addCase(removeInvite.pending, (state, action) => {
      state.removingInviteEmail = action.meta.arg;
      state.isRemovingInvite = true;
      state.hasRemovedInvite = false;
      state.removeInviteError = null;
    });
    builder.addCase(removeInvite.fulfilled, state => {
      state.removingInviteEmail = null;
      state.isRemovingInvite = false;
      state.hasRemovedInvite = true;
    });
    builder.addCase(removeInvite.rejected, (state, action) => {
      state.removingInviteEmail = null;
      state.hasRemovedInvite = false;
      state.isRemovingInvite = false;
      state.removeInviteError = action.error.message || null;
    });

    // saveOrgIntegration
    const integrations = [
      saveOrgIntegration,
      saveClearbitIntegration,
      saveSixsenseIntegration,
      saveChiliPiperIntegration,
      saveHubSpotMeetingsIntegration,
    ];
    addManyCases(
      builder,
      integrations.map(x => x.pending),
      state => {
        state.isSavingOrgIntegrations = true;
        state.hasSavedOrgIntegration = false;
        state.saveOrgIntegrationError = null;
      },
    );
    addManyCases(
      builder,
      integrations.map(x => x.fulfilled),
      state => {
        state.isSavingOrgIntegrations = false;
        state.hasSavedOrgIntegration = true;
      },
    );
    addManyCases(
      builder,
      integrations.map(x => x.rejected),
      (state, action) => {
        state.isSavingOrgIntegrations = false;
        state.saveOrgIntegrationError = action.error.message || null;
      },
    );

    // addDomain
    builder.addCase(addDomain.pending, state => {
      state.isAddingDomain = true;
      state.hasAddedDomain = false;
      state.addDomainError = null;
    });
    builder.addCase(addDomain.fulfilled, state => {
      state.isAddingDomain = false;
      state.hasAddedDomain = true;
    });
    builder.addCase(addDomain.rejected, (state, action) => {
      state.isAddingDomain = false;
      state.addDomainError = action.error.message || null;
    });

    // removeDomain
    builder.addCase(removeDomain.pending, state => {
      state.isRemovingDomain = true;
      state.hasRemovedDomain = false;
      state.removeDomainError = null;
    });
    builder.addCase(removeDomain.fulfilled, state => {
      state.isRemovingDomain = false;
      state.hasRemovedDomain = true;
    });
    builder.addCase(removeDomain.rejected, (state, action) => {
      state.isRemovingDomain = false;
      state.removeDomainError = action.error.message || null;
    });

    // updateOrgSettings
    builder.addCase(updateOrgSettings.pending, state => {
      state.isUpdatingSettings = true;
      state.hasUpdatedSettings = false;
      state.updateSettingsError = null;
    });
    builder.addCase(updateOrgSettings.fulfilled, state => {
      state.isUpdatingSettings = false;
      state.hasUpdatedSettings = true;
    });
    builder.addCase(updateOrgSettings.rejected, (state, action) => {
      state.isUpdatingSettings = false;
      state.updateSettingsError = action.error.message || null;
    });

    // removeMember
    builder.addCase(removeMember.pending, state => {
      state.isRemovingMember = true;
      state.removeMemberError = null;
    });
    builder.addCase(removeMember.fulfilled, state => {
      state.isRemovingMember = false;
    });
    builder.addCase(removeMember.rejected, (state, action) => {
      state.isRemovingMember = false;
      state.removeMemberError = action.error.message || null;
    });

    // loadCustomProperties
    builder.addCase(loadCustomProperties.fulfilled, (state, action) => {
      state.customProperties = action.payload;
    });
  },
});

// SELECTORS
// ...

export const {
  clearInviteUserEphemeralState,
  setOrgProspectProperties,
  setOrgProspectPropertiesMeta,
} = orgSlice.actions;
export {
  updateTeamMember,
  saveOrgIntegration,
  updateOrgIntegration,
  addDomain,
  removeDomain,
  updateOrgSettings,
  removeMember,
};
export default orgSlice.reducer;
